import { useCallback, useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { PageHeader, StatusPill } from "@/components/admin/PageHeader";
import {
  adminEnquiryDeliveryFn,
  adminListEnquiriesFn,
  adminRetryEnquiryEmailFn,
  adminSetEnquiryStatusFn,
} from "@/lib/enquiries/enquiries.functions";
import {
  EMAIL_STATUS_LABEL,
  ENQUIRY_STATUSES,
  ENQUIRY_STATUS_LABEL,
  formatIndiaTime,
  replyMailto,
  type EnquiryStatus,
} from "@/lib/enquiries/rules";

type Enquiry = Awaited<ReturnType<typeof adminListEnquiriesFn>>["enquiries"][number];

export const Route = createFileRoute("/admin/enquiries")({
  head: () => ({
    meta: [
      { title: "Customer Enquiries — CB16 Admin" },
      { name: "description", content: "Contact page enquiries for CB16: read, reply and track status." },
      { property: "og:title", content: "Customer Enquiries — CB16 Admin" },
      { property: "og:description", content: "Contact page enquiries for CB16: read, reply and track status." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: EnquiriesPage,
});

const statusTone = (s: EnquiryStatus) => (s === "new" ? "warn" : s === "in_progress" ? "info" : "success");
const emailTone = (s: Enquiry["emailStatus"]) => (s === "sent" ? "success" : s === "failed" ? "danger" : "neutral");

function EnquiriesPage() {
  const list = useServerFn(adminListEnquiriesFn);
  const setStatusFn = useServerFn(adminSetEnquiryStatusFn);
  const retryFn = useServerFn(adminRetryEnquiryEmailFn);
  const deliveryFn = useServerFn(adminEnquiryDeliveryFn);
  const [rows, setRows] = useState<Enquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<"all" | EnquiryStatus>("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [openRef, setOpenRef] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await list();
      setRows(r.enquiries);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load enquiries.");
    } finally {
      setLoading(false);
    }
  }, [list]);
  useEffect(() => { void load(); }, [load]);

  const replace = (e: Enquiry) => setRows((rs) => rs.map((r) => (r.reference === e.reference ? e : r)));

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const fromT = from ? new Date(`${from}T00:00:00+05:30`).getTime() : null;
    const toT = to ? new Date(`${to}T23:59:59.999+05:30`).getTime() : null;
    return rows.filter((r) => {
      if (status !== "all" && r.status !== status) return false;
      if (needle && !r.name.toLowerCase().includes(needle) && !r.email.toLowerCase().includes(needle)) return false;
      const t = new Date(r.createdAt).getTime();
      if (fromT !== null && t < fromT) return false;
      if (toT !== null && t > toT) return false;
      return true;
    });
  }, [rows, q, status, from, to]);

  const newCount = rows.filter((r) => r.status === "new").length;
  const open = rows.find((r) => r.reference === openRef) ?? null;

  async function changeStatus(e: Enquiry, s: EnquiryStatus) {
    setBusy(e.reference);
    setNotice(null);
    try {
      replace(await setStatusFn({ data: { reference: e.reference, status: s } }));
      setNotice(`${e.reference} marked ${ENQUIRY_STATUS_LABEL[s]}.`);
    } catch (err) {
      setNotice(err instanceof Error ? err.message : "Could not update status.");
    } finally {
      setBusy(null);
    }
  }

  async function retry(e: Enquiry) {
    setBusy(e.reference);
    setNotice(null);
    try {
      const next = await retryFn({ data: { reference: e.reference } });
      replace(next);
      setNotice(next.emailStatus === "sent" ? `Notification for ${e.reference} accepted by ${next.emailProvider ?? "the provider"}.` : `Notification for ${e.reference} failed again: ${next.emailError ?? "unknown error"}`);
    } catch (err) {
      setNotice(err instanceof Error ? err.message : "Retry failed.");
    } finally {
      setBusy(null);
    }
  }

  async function checkDelivery(e: Enquiry) {
    setBusy(e.reference);
    try {
      const r = await deliveryFn({ data: { reference: e.reference } });
      setNotice(`${e.reference}: ${r.note}`);
    } catch (err) {
      setNotice(err instanceof Error ? err.message : "Could not check delivery.");
    } finally {
      setBusy(null);
    }
  }

  const input = "border border-black/15 bg-white px-2 py-2 text-sm";
  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Customer Enquiries"
        subtitle="Messages sent from the Contact page. Notifications go to the Support Email in Settings → Contact."
        action={<span className="text-sm text-black/70"><strong className="tabular-nums">{newCount}</strong> new</span>}
      />

      <div className="flex flex-wrap items-end gap-3 border border-black/10 bg-white p-4">
        <label className="grid gap-1 text-[10px] font-semibold uppercase tracking-widest text-black/60">
          Search name or email
          <input aria-label="Search name or email" className={input + " w-64 normal-case tracking-normal"} value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. aditya" />
        </label>
        <label className="grid gap-1 text-[10px] font-semibold uppercase tracking-widest text-black/60">
          Status
          <select aria-label="Status filter" className={input} value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
            <option value="all">All</option>
            {ENQUIRY_STATUSES.map((s) => <option key={s} value={s}>{ENQUIRY_STATUS_LABEL[s]}</option>)}
          </select>
        </label>
        <label className="grid gap-1 text-[10px] font-semibold uppercase tracking-widest text-black/60">
          From
          <input aria-label="From date" type="date" className={input} value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="grid gap-1 text-[10px] font-semibold uppercase tracking-widest text-black/60">
          To
          <input aria-label="To date" type="date" className={input} value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <button type="button" onClick={() => void load()} className="border border-black px-4 py-2 text-[11px] font-semibold uppercase tracking-widest">Refresh</button>
      </div>

      {notice && <p role="status" className="border border-[#C6A15B]/40 bg-[#C6A15B]/10 px-4 py-2 text-sm">{notice}</p>}
      {error && <p role="alert" className="border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-800">{error}</p>}

      <div className="overflow-x-auto border border-black/10 bg-white">
        <table className="w-full min-w-[960px] text-left text-sm">
          <thead className="border-b border-black/10 text-[10px] uppercase tracking-widest text-black/60">
            <tr>
              <th className="px-3 py-2">Enquiry ID</th><th className="px-3 py-2">Customer</th><th className="px-3 py-2">Subject</th>
              <th className="px-3 py-2">Message</th><th className="px-3 py-2">Submitted</th><th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Email</th><th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-3 py-6 text-center text-black/50">Loading…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center text-black/50">No enquiries match.</td></tr>}
            {!loading && filtered.map((r) => (
              <tr key={r.reference} className="border-b border-black/5 align-top" data-enquiry={r.reference}>
                <td className="px-3 py-2 font-mono text-xs">{r.reference}</td>
                <td className="px-3 py-2"><div className="font-medium">{r.name}</div><div className="text-xs text-black/60">{r.email}</div></td>
                <td className="px-3 py-2">{r.subject}</td>
                <td className="max-w-[240px] px-3 py-2 text-black/70"><span className="line-clamp-2">{r.message}</span></td>
                <td className="whitespace-nowrap px-3 py-2 text-xs">{formatIndiaTime(r.createdAt)}</td>
                <td className="px-3 py-2"><StatusPill tone={statusTone(r.status)}>{ENQUIRY_STATUS_LABEL[r.status]}</StatusPill></td>
                <td className="px-3 py-2"><StatusPill tone={emailTone(r.emailStatus)}>{EMAIL_STATUS_LABEL[r.emailStatus]}</StatusPill></td>
                <td className="px-3 py-2"><button type="button" onClick={() => setOpenRef(r.reference)} className="text-[11px] font-semibold uppercase tracking-widest underline">View</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div role="dialog" aria-modal="true" aria-label={`Enquiry ${open.reference}`} className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setOpenRef(null)}>
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto bg-white p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-mono text-xs text-black/60">{open.reference}</p>
                <h3 className="mt-1 text-xl font-semibold">{open.subject}</h3>
                <p className="mt-1 text-sm text-black/70">{open.name} · {open.email}</p>
                <p className="text-xs text-black/50">Submitted {formatIndiaTime(open.createdAt)}</p>
              </div>
              <button type="button" onClick={() => setOpenRef(null)} className="text-sm underline">Close</button>
            </div>
            {notice && <p role="status" className="mt-3 border border-[#C6A15B]/40 bg-[#C6A15B]/10 px-3 py-2 text-xs">{notice}</p>}
            <div className="mt-4 whitespace-pre-wrap border-l-2 border-[#C6A15B] px-4 py-2 text-sm leading-relaxed" data-testid="enquiry-message">{open.message}</div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-widest text-black/60">
                Status
                <select aria-label="Enquiry status" className={input} value={open.status} disabled={busy === open.reference}
                  onChange={(e) => void changeStatus(open, e.target.value as EnquiryStatus)}>
                  {ENQUIRY_STATUSES.map((s) => <option key={s} value={s}>{ENQUIRY_STATUS_LABEL[s]}</option>)}
                </select>
              </label>
              <a href={replyMailto(open.email, open.subject, open.reference)} className="border border-black bg-black px-4 py-2 text-[11px] font-semibold uppercase tracking-widest text-white">Reply by email</a>
            </div>

            <div className="mt-6 border border-black/10 p-4 text-sm">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-black/60">Email notification</p>
              <div className="mt-2 grid gap-1">
                <p>Status: <StatusPill tone={emailTone(open.emailStatus)}>{EMAIL_STATUS_LABEL[open.emailStatus]}</StatusPill></p>
                <p>Sent to: {open.emailRecipient ?? "—"}</p>
                <p>Provider: {open.emailProvider ?? "—"}{open.emailMessageId ? ` · message ID ${open.emailMessageId}` : ""}</p>
                <p>Attempts: {open.emailAttempts}{open.emailLastAttemptAt ? ` · last ${formatIndiaTime(open.emailLastAttemptAt)}` : ""}</p>
                {open.emailError && <p className="text-red-700">Error: {open.emailError}</p>}
                <p className="text-xs text-black/50">"Accepted by provider" means the email service took the message; inbox delivery is confirmed in the inbox itself.</p>
              </div>
              <div className="mt-3 flex flex-wrap gap-3">
                {open.canRetry && (
                  <button type="button" disabled={busy === open.reference} onClick={() => void retry(open)} className="border border-black px-4 py-2 text-[11px] font-semibold uppercase tracking-widest disabled:opacity-50">
                    {open.emailStatus === "failed" ? "Retry notification" : "Send notification"}
                  </button>
                )}
                {open.emailMessageId && (
                  <button type="button" disabled={busy === open.reference} onClick={() => void checkDelivery(open)} className="border border-black/30 px-4 py-2 text-[11px] font-semibold uppercase tracking-widest disabled:opacity-50">Check delivery</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
